import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, KeyRound } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import RecoveryCodeModal from '@/components/RecoveryCodeModal';
import LegalFooter from '@/components/LegalFooter';

/**
 * Used by two flows:
 *  - /set-password?token=...  (account auto-created via ClickFunnels purchase)
 *  - /reset/email?token=...   (password reset via email magic link)
 *
 * Both endpoints POST {token, new_password} and return {token, user, recovery_code}.
 */
export default function SetPassword({ mode = 'reset' }) {
    const { setUser } = useAuth();
    const [params] = useSearchParams();
    const token = params.get('token') || '';
    const navigate = useNavigate();
    const [pw, setPw] = useState('');
    const [show, setShow] = useState(false);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [newCode, setNewCode] = useState(null);

    useEffect(() => {
        if (!token) setError('Missing or invalid link.');
    }, [token]);

    const endpoint = mode === 'set-password' ? '/auth/set-password' : '/auth/reset/email-confirm';

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);
        try {
            const { data } = await api.post(endpoint, { token, new_password: pw });
            localStorage.setItem('qm_token', data.token);
            setUser(data.user);
            setNewCode(data.recovery_code);
        } catch (err) {
            setError(err?.response?.data?.detail || 'Could not set your password.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-background relative overflow-hidden flex flex-col">
            <div className="pointer-events-none absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-sage/40 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-40 -right-40 w-[460px] h-[460px] rounded-full bg-dusty/50 blur-3xl" />

            <div className="relative z-10 flex-1 grid place-items-center px-6 py-12">
                <div className="w-full max-w-md" data-testid="set-password-page">
                    <Link to="/auth" className="inline-flex items-center gap-2 text-forest-soft hover:text-forest text-sm mb-8">
                        <ArrowLeft size={16} /> Back to sign in
                    </Link>
                    <div className="w-12 h-12 rounded-2xl bg-sage/40 grid place-items-center mb-3">
                        <KeyRound size={22} className="text-forest" />
                    </div>
                    <h1 className="font-serif text-4xl text-forest">
                        {mode === 'set-password' ? 'Set your password' : 'Choose a new password'}
                    </h1>
                    <p className="text-forest-soft mt-2">
                        {mode === 'set-password'
                            ? 'Welcome — your purchase has been received. Pick a password to start practicing.'
                            : 'Pick a new password. We’ll log you in and rotate your recovery code.'}
                    </p>

                    <form onSubmit={submit} className="mt-8 space-y-4" data-testid="set-password-form">
                        <div>
                            <label className="text-sm text-forest-soft">New password</label>
                            <div className="relative mt-1">
                                <input
                                    type={show ? 'text' : 'password'} minLength={6} required
                                    value={pw} onChange={(e) => setPw(e.target.value)}
                                    placeholder="At least 6 characters"
                                    className="qm-input pr-12 w-full"
                                    data-testid="set-password-input"
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button" onClick={() => setShow((s) => !s)}
                                    aria-label={show ? 'Hide password' : 'Show password'}
                                    data-testid="set-password-toggle"
                                    className="absolute inset-y-0 right-2 my-auto h-9 w-9 grid place-items-center rounded-full text-forest-soft hover:bg-sage/30 hover:text-forest"
                                >
                                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3" data-testid="set-password-error">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit" disabled={submitting || !token}
                            data-testid="set-password-submit"
                            className="qm-btn-primary w-full disabled:opacity-60"
                        >
                            {submitting ? 'A moment …' : 'Set my password'}
                        </button>
                    </form>
                </div>
            </div>

            <LegalFooter />

            {newCode && (
                <RecoveryCodeModal
                    code={newCode}
                    onClose={() => navigate('/app/home', { replace: true })}
                    title="Save your recovery code"
                    intro="Welcome! Here’s your one-time recovery code — please save it somewhere safe."
                />
            )}
        </div>
    );
}
